import {
  TRANSFORMATION_STATUS_LABELS,
  type StockTransformation,
} from "@artisancode/api-types";
import {
  AlertCircle,
  ArrowDownRight,
  Calendar,
  CheckCircle2,
  Clock,
  Package,
  Scale,
  User,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useHasPermission } from "@/hooks/use-auth";
import { formatDateWIB } from "@/lib/date-utils";
import { formatCurrency } from "@/lib/utils";

import { RepackingOutputLines } from "./repacking-output-lines";
import { summarizeRepacking } from "./repacking-utils";

interface Props {
  transformation: StockTransformation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (id: string) => void;
  onCancelRequest?: (trans: StockTransformation) => void;
  isCompleting?: boolean;
}

export function RepackingDetailDialog({
  transformation,
  open,
  onOpenChange,
  onComplete,
  onCancelRequest,
  isCompleting,
}: Props) {
  const canComplete = useHasPermission("stock_transformations.complete");
  if (!transformation) return null;

  const statusMeta = TRANSFORMATION_STATUS_LABELS[transformation.status];
  const { inputLine, outputLine, outputUom, expectedQty, shrinkagePercent } =
    summarizeRepacking(transformation);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <DialogTitle className="text-base font-semibold">
                Detail Repacking {transformation.code}
              </DialogTitle>
            </div>
            <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
          </div>
          <DialogDescription className="text-xs">
            Kemasan yang dibuka dari gudang dan hasilnya di bar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-xs">
          {/* Metadata Card */}
          <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Tanggal:</span>
              <p className="mt-0.5 font-medium flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                {formatDateWIB(transformation.date)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Dibuat Oleh:</span>
              <p className="mt-0.5 font-medium flex items-center gap-1">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                {transformation.createdByName || "-"}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Waktu Selesai:</span>
              <p className="mt-0.5 font-medium flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                {transformation.completedAt
                  ? formatDateWIB(transformation.completedAt)
                  : "-"}
              </p>
            </div>
          </div>

          {/* Source Input */}
          <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="font-semibold text-foreground flex items-center gap-1.5">
                <ArrowDownRight className="h-4 w-4 text-amber-500" />
                Dibuka dari Gudang
              </span>
              <Badge variant="outline" className="text-[10px]">
                Wadah: On-Hand
              </Badge>
            </div>
            {inputLine ? (
              <div className="mt-2.5 flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">
                    {inputLine.itemName}
                  </p>
                  {inputLine.itemSku && (
                    <p className="text-[11px] text-muted-foreground font-mono">
                      SKU: {inputLine.itemSku}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-foreground text-sm">
                    {inputLine.qty.toLocaleString("id-ID")} {inputLine.uomName}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    @ {formatCurrency(inputLine.unitCost)} / {inputLine.uomName}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-muted-foreground">Tidak ada data input</p>
            )}
          </div>

          {outputLine && <RepackingOutputLines line={outputLine} />}

          {/* Shrinkage & Reconciliation Summary */}
          <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
            <div className="flex items-center justify-between font-medium">
              <span className="text-muted-foreground">
                Seharusnya (isi kemasan):
              </span>
              <span>
                {expectedQty.toLocaleString("id-ID")} {outputUom}
              </span>
            </div>
            <div className="flex items-center justify-between font-medium">
              <span className="text-muted-foreground">Hasil (Masuk Bar):</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                {transformation.totalOutputQty.toLocaleString("id-ID")}{" "}
                {outputUom}
              </span>
            </div>
            <div className="flex items-center justify-between font-medium border-t pt-2">
              <span className="text-muted-foreground flex items-center gap-1">
                <Scale className="h-3.5 w-3.5 text-rose-500" />
                Susut Proses / Shrinkage:
              </span>
              <span
                className={`font-semibold ${
                  transformation.shrinkageQty > 0
                    ? "text-rose-600 dark:text-rose-400"
                    : "text-muted-foreground"
                }`}
              >
                {transformation.shrinkageQty.toLocaleString("id-ID")}{" "}
                {outputUom} ({shrinkagePercent}%)
              </span>
            </div>
          </div>

          {/* Notes & Cancel Reason */}
          {transformation.note && (
            <div className="rounded-md border p-2.5">
              <span className="font-semibold text-muted-foreground block mb-0.5">
                Catatan:
              </span>
              <p className="text-foreground">{transformation.note}</p>
            </div>
          )}

          {transformation.status === "cancelled" &&
            transformation.cancelReason && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-destructive">
                <span className="font-semibold block mb-0.5 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Alasan Pembatalan:
                </span>
                <p>{transformation.cancelReason}</p>
              </div>
            )}
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:gap-2">
          <div>
            {transformation.status === "draft" && onCancelRequest && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto text-destructive hover:bg-destructive/10 text-xs min-h-[40px] sm:min-h-9"
                onClick={() => {
                  onOpenChange(false);
                  onCancelRequest(transformation);
                }}
              >
                Batalkan Draft
              </Button>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="min-h-[40px] sm:min-h-9"
            >
              Tutup
            </Button>
            {transformation.status === "draft" && onComplete && canComplete && (
              <Button
                type="button"
                size="sm"
                disabled={isCompleting}
                onClick={() => onComplete(transformation.id)}
                className="gap-1.5 min-h-[40px] sm:min-h-9"
              >
                <CheckCircle2 className="h-4 w-4" />
                {isCompleting ? "Menyelesaikan..." : "Selesaikan Repacking"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
